import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getInspectionId,
  getPersistedDifferenceComments,
  inspection,
  getFocusedDifferenceId,
  getGroupedDifferences,
} from 'store';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import GVDifferenceButtonSet from 'components/lib/GVDifferenceButtonSet/GVDifferenceButtonSet';
import { updateDifference } from 'store/request/differences/actions';
import { patchGroup } from 'store/request/difference-group/actions';
import { Difference } from 'store/request/differences/types';
import { useTracker } from '../../Tracker/TrackerProvider';
import { isFalsePositiveComment } from './constants';

interface DifferenceElementProps {
  difference: Difference;
  numberOfDifference: number;
  handleToggleComment: (e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>, open: boolean) => void;
  onSaveComment: (comment: string) => void;
}

const DifferenceComments = ({
  difference,
  numberOfDifference,
  handleToggleComment,
  onSaveComment,
}: DifferenceElementProps) => {
  const tracker = useTracker();
  const dispatch = useDispatch();
  const savedComment = difference.comment ?? '';
  const inspectionId = useSelector(getInspectionId);
  const differenceComments = useSelector(getPersistedDifferenceComments);
  const focusedDifferenceId = useSelector(getFocusedDifferenceId);
  const group = useSelector(getGroupedDifferences(difference.groupId));
  const persistedComment = differenceComments[difference.id];
  const inputRef = useRef<HTMLInputElement>(null);
  const commentRef = useRef<string>(''); // used for accessing input on component unmount
  const [comment, setComment] = useState<string>(persistedComment ?? savedComment);
  const [showButtons, setShowButtons] = useState<boolean>(false);

  useEffect(() => {
    commentRef.current = comment;
  }, [comment]);

  useEffect(() => {
    // Keep Save/Cancel buttons in sync with current value
    setShowButtons(comment !== savedComment);
  }, [comment, savedComment]);

  useEffect(() => {
    if (focusedDifferenceId === difference.id) {
      inputRef.current?.focus();
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [focusedDifferenceId, difference.id]);

  useEffect(() => {
    return () => {
      dispatch(
        inspection.actions.setPersistedComment({
          diffIds: [difference.id],
          comment: commentRef.current,
        }),
      );
    };
  }, [dispatch, difference.id]);

  useEffect(() => {
    setComment(persistedComment ?? savedComment ?? '');
  }, [savedComment, persistedComment]);

  const handleResetComment = (e: React.MouseEvent) => {
    // Cancel should discard any draft edits. Because closing the panel unmounts this
    // component, we must update the persisted draft synchronously to avoid an
    // unmount race where the cleanup effect writes a stale `commentRef.current`.
    commentRef.current = savedComment;
    dispatch(
      inspection.actions.setPersistedComment({
        diffIds: [difference.id],
        comment: savedComment,
      }),
    );
    setComment(savedComment);
    if (inputRef.current) {
      inputRef.current.blur();
    }
    setShowButtons(false);
    handleToggleComment(e, false);
  };

  const handleSaveComment = (e: React.MouseEvent | React.KeyboardEvent) => {
    const wasFalsePositive = isFalsePositiveComment(savedComment);
    const willBeFalsePositive = isFalsePositiveComment(comment);

    if (difference.groupId) {
      dispatch(patchGroup(inspectionId, difference.groupId, { comment }));
    } else {
      dispatch(updateDifference(difference.id, { comment }));
    }

    dispatch(
      inspection.actions.setPersistedComment({
        diffIds: [difference.id],
        comment: commentRef.current,
      }),
    );

    tracker.track({
      name: 'difference-commented',
      data: {
        differenceComment: comment,
        differenceId: difference.groupId ? difference.groupId : difference.id,
        differenceType: difference.type,
        groupType: difference.groupId ? 'group' : 'single',
      },
    });

    // Track false positive flagging/unflagging only when the user saves.
    if (!wasFalsePositive && willBeFalsePositive) {
      tracker.track({
        name: 'false-positive-flagged',
        data: {
          count: difference.groupId ? group?.length || 1 : 1,
          groupId: difference.groupId,
          differenceId: difference.id,
          type: difference.type,
        },
      });
    } else if (wasFalsePositive && !willBeFalsePositive) {
      tracker.track({
        name: 'false-positive-unflagged',
        data: {
          count: difference.groupId ? group?.length || 1 : 1,
          groupId: difference.groupId,
          differenceId: difference.id,
          type: difference.type,
        },
      });
    }

    if (inputRef.current) {
      inputRef.current.blur();
    }

    setShowButtons(false);
    handleToggleComment(e, false);
    if (onSaveComment) {
      onSaveComment(comment);
    }
  };

  const handleCommentKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveComment(e);
    } else if (e.key === 'Escape') {
      // Mirror Cancel behavior: discard draft edits and prevent stale draft
      // from being persisted on unmount.
      commentRef.current = savedComment;
      dispatch(
        inspection.actions.setPersistedComment({
          diffIds: [difference.id],
          comment: savedComment,
        }),
      );
      setComment(savedComment);
      if (inputRef.current) {
        inputRef.current.blur();
      }
      setShowButtons(false);
    }
  };

  const handleOnChange = (value: string | null) => {
    setComment(value ?? '');
    setShowButtons(true);
  };

  const handleOnFocus = (event?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = event?.target;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLTextAreaElement)) {
      return;
    }
    // place cursor at end instead of beginning of input on autofocus
    target.setSelectionRange(target.value.length, target.value.length);
  };

  return (
    <>
      <GVTextField
        multiline
        value={comment}
        label="Add a comment"
        ref={inputRef}
        id={`commentInput-${numberOfDifference + 1}`}
        onValueChange={handleOnChange}
        onKeyDown={handleCommentKeyDown}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onFocus={handleOnFocus}
        isAutoSized
      />
      {showButtons && (
        <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <GVDifferenceButtonSet
            handleCancel={handleResetComment}
            handleSave={handleSaveComment}
            stringNumberForId={`${numberOfDifference + 1}`}
          />
        </div>
      )}
    </>
  );
};

export default DifferenceComments;
